// Thin wrapper over the pancakestack Lambda Function URL.
// The AuthContext supplies the id_token; this module is stateless.
//
// All bodies are JSON. Errors normalize into ApiError{code, message, status}.

import { environment } from '../config/environment';

const BASE = environment.apiBaseUrl;

export class ApiError extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

type FetchOpts = { token?: string | null; signal?: AbortSignal };

const request = async <T>(
  method: string,
  path: string,
  body: unknown,
  opts: FetchOpts = {},
): Promise<T> => {
  if (!BASE) throw new ApiError(0, 'NO_API_BASE_URL', 'REACT_APP_API_BASE_URL is not configured');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;
  const resp = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: opts.signal,
  });
  const text = await resp.text();
  const json = text ? safeJson(text) : null;
  if (!resp.ok) {
    const code = (json && (json as any).code) || `HTTP_${resp.status}`;
    const msg  = (json && (json as any).error) || text || resp.statusText;
    throw new ApiError(resp.status, code, msg);
  }
  return (json as T) ?? (undefined as unknown as T);
};

const safeJson = (t: string): unknown => {
  try { return JSON.parse(t); } catch { return null; }
};

// ---- Types (mirror Lambda contracts) ----

export type ScriptId = 'seestar' | 'seestar-drizzle' | 'seestar-mosaic';

export interface CatalogParamDef {
  name: string;
  type: 'float' | 'int' | 'bool';
  default: number | boolean;
  min?: number;
  max?: number;
  // step is the slider granularity. Falls back to a heuristic on the
  // client if the backend didn't specify one.
  step?: number;
  enum?: number[];
  label?: string;
  description?: string;
}
export interface CatalogEntry {
  id: ScriptId;
  title: string;
  summary: string;
  params: CatalogParamDef[];
}
export interface InstanceOption {
  type: string;
  vcpu: number;
  ramGB: number;
  approxSpotHourly: number;
  recommendedFor: string;
}

export interface UploadInitReq {
  collectionId: string;
  filename: string;           // .tar / .tar.gz / .tgz / .tar.zst / .zip
  contentType?: string;
  // SHA-256 of the file bytes, lowercase hex. Required — backend uses
  // it to reject duplicates before we even start streaming parts.
  sha256: string;
}
export interface UploadInitResp {
  uploadId?: string;
  key?: string;               // S3 object key we're building
  partSize?: number;          // recommended part size in bytes
  // Backend returns skipped=true when a file with this checksum
  // already exists in the collection. Caller must not start parts.
  skipped?: boolean;
  reason?: string;            // "duplicate"
  duplicateOf?: string;       // existing filename in same collection
}
export interface UploadPartReq {
  uploadId: string;
  key: string;
  partNumber: number;         // 1-based
}
export interface UploadPartResp {
  url: string;                // presigned PUT URL for this part
}
export interface CompletedPart { partNumber: number; etag: string; }
export interface UploadCompleteReq {
  uploadId: string;
  key: string;
  parts: CompletedPart[];
}
export interface UploadCompleteResp {
  key?: string;
  s3Uri?: string;             // s3://bucket/key
  sizeBytes?: number;
  // Synthesized by multipartUpload.ts when initUpload returns skipped;
  // never sent by the backend directly for /upload/complete.
  skipped?: boolean;
  reason?: string;
  duplicateOf?: string;
}

export interface StartJobReq {
  collectionId: string;
  scriptId: ScriptId;
  // Optional free-form label for this stack (e.g. "30s 3x drizzle"). Trimmed
  // + length-capped server-side (see nameMaxLen in the Lambda). UI falls back
  // to collectionId when empty.
  name?: string;
  params?: Record<string, number | boolean>;
  // Optional filename allowlist — when present, only these files (relative
  // to the collection prefix) are stacked. Omit to stack the whole prefix.
  // Populated from the CollectionDetail filter flow so the user can cull
  // bad frames before spending EC2 time on them.
  includeFiles?: string[];
}
export interface StartJobResp {
  jobId: string;
  runId: string;
  instanceId: string;
  status: 'launched';
  scriptId: ScriptId;
  effectiveParams: Record<string, number | boolean>;
  inputPrefix: string;
  outputPrefix: string;
}

export type JobState =
  | 'uploading'
  | 'uploaded'
  | 'created'     // record inserted, EC2 launch in flight
  | 'running'     // EC2 instance up; sub-stage in `stage`
  | 'succeeded'
  | 'failed'
  | 'cancelled';

export interface JobSummary {
  jobId: string;
  runId: string;
  collectionId: string;
  // Optional user-supplied label. Falls back to collectionId when absent.
  name?: string;
  scriptId: ScriptId;
  instanceType: string;
  createdAt: string;          // ISO
  state: JobState;
  // Human-readable sub-stage while status=running. e.g. "Installing Siril",
  // "Stacking", "Uploading result". Set by the EC2 user-data at each
  // stage boundary. Absent for terminal states.
  stage?: string;
  stageStartedAt?: string;    // ISO — when the current stage began
  // Billable window written by the EC2 user-data: startedAt at "running",
  // endedAt at terminal state. runtimeSeconds is denormalized so cost calcs
  // don't have to parse timestamps. Absent while the job is still in
  // created/uploading — endedAt+runtimeSeconds absent until terminal.
  startedAt?: string;
  endedAt?: string;
  runtimeSeconds?: number;
  outputPrefix?: string;
  resultKey?: string;
  error?: string;
  // Optional filename allowlist supplied when the job started ("stack N
  // filtered frames" flow). Absent = every FITS in the collection was
  // used. JobDetail reads this to compute exact frame count + integration.
  includeFiles?: string[];
}

// ---- Public functions ----

/** GET /profile — plan, stacking-minutes balance, storage quota. Lazy-grants
 *  the monthly minutes refill on first call each UTC month. One "stacking
 *  minute" = one minute of r7g.16xlarge runtime. */
export interface ProfileResp {
  plan: string;
  stackingMinutes: {
    balance: number;
    monthlyGrant: number;
    nextGrantAt: string;
  };
  storage: {
    // Chargeable footprint: workingBytes + archivedBytes * archiveDiscount.
    // This is the value the quota bar compares against quotaBytes.
    usedBytes: number;
    // Raw component sums — the dashboard stacks them as two colored
    // segments (working: teal, archive: violet). archiveDiscount tells
    // the UI what multiplier the backend applied so tooltips can show
    // "archived: 100 GB (counts as 70 GB)".
    workingBytes: number;
    archivedBytes: number;
    archiveDiscount: number;
    quotaBytes: number;
  };
}
export const getProfile = (opts: FetchOpts) =>
  request<ProfileResp>('GET', '/profile', undefined, opts);

/** POST /invites/redeem — claim a one-time invite code. Authed but does
 *  NOT require an approved-group membership (this is the one call an
 *  unapproved user can make to move themselves onto the allowlist).
 *  Client MUST refresh their ID token after `needsRefresh=true` — the
 *  token they're holding was issued before the new group was granted. */
export const redeemInvite = (code: string, opts: FetchOpts) =>
  request<{ ok: boolean; group: string; needsRefresh: boolean }>(
    'POST', '/invites/redeem', { code }, opts,
  );

/** GET /catalog — script definitions + instance whitelist. Unauthed. */
export const getCatalog = (opts: FetchOpts = {}) =>
  request<{ scripts: CatalogEntry[]; instances: InstanceOption[] }>(
    'GET', '/catalog', undefined, opts,
  );

/** GET /stats — public site-wide aggregates for the marketing widget. */
export interface StatsResponse {
  updatedAt: string;
  stacksCompleted: number;
  stacksTotal: number;
  lightFrames: number;
  collections: number;
  storageBytes: number;
  integrationSeconds: number;
  computeSeconds: number;
  astronomers: number;
  /** Ops message — surfaced as the yellow site-wide banner. Empty/undefined
   *  when nothing is going on. Sourced from the statName=ops row (see
   *  lambda-start-job/stats.go and infra/code/dynamodb_stats.tf). */
  banner?: string;
}
export const getStats = (opts: FetchOpts = {}) =>
  request<StatsResponse>('GET', '/stats', undefined, opts);

/** POST /upload/init — begin multipart upload. */
export const initUpload = (body: UploadInitReq, opts: FetchOpts) =>
  request<UploadInitResp>('POST', '/upload/init', body, opts);

/** POST /upload/part — presign a single UploadPart URL. */
export const signPart = (body: UploadPartReq, opts: FetchOpts) =>
  request<UploadPartResp>('POST', '/upload/part', body, opts);

/** POST /upload/complete — finalize the object. */
export const completeUpload = (body: UploadCompleteReq, opts: FetchOpts) =>
  request<UploadCompleteResp>('POST', '/upload/complete', body, opts);

/** POST /jobs — launch a stacking job on the given collection. */
export const startJob = (body: StartJobReq, opts: FetchOpts) =>
  request<StartJobResp>('POST', '/jobs', body, opts);

/** GET /jobs — list caller's jobs, newest first. */
export const listJobs = (opts: FetchOpts) =>
  request<{ jobs: JobSummary[] }>('GET', '/jobs', undefined, opts);

/** GET /jobs/{id} — job detail. downloadUrl + resultFilename are only
 *  present when the job is state=succeeded and a result file was produced. */
export interface JobDetail extends JobSummary {
  effectiveParams: Record<string, number | boolean>;
  downloadUrl?: string;
  resultFilename?: string;
  // Signed URL to an autostretched preview JPEG rendered by Siril
  // alongside the master. ~1-2 MB, safe to display in an <img>. Absent
  // means the preview render failed or hasn't been produced (older jobs).
  previewUrl?: string;
}
export const getJob = (id: string, opts: FetchOpts) =>
  request<JobDetail>('GET', `/jobs/${encodeURIComponent(id)}`, undefined, opts);

/** POST /jobs/{id}/cancel — terminate the EC2 instance (if any) and mark the
 *  job cancelled. Only works while state ∈ {created, running, uploading,
 *  uploaded}; returns 400 NOT_CANCELLABLE for terminal states. */
export const cancelJob = (id: string, opts: FetchOpts) =>
  request<JobDetail>('POST', `/jobs/${encodeURIComponent(id)}/cancel`, {}, opts);

/** POST /jobs/{id}/retry — re-launch a terminal job with the same
 *  collectionId, script, params, instance, and filename. Backend delegates
 *  to the same handler as POST /jobs; returns the NEW job's launch response
 *  (fresh jobId). Only allowed on failed/cancelled/succeeded jobs. */
export const retryJob = (id: string, opts: FetchOpts) =>
  request<StartJobResp>('POST', `/jobs/${encodeURIComponent(id)}/retry`, {}, opts);

/** POST /jobs/{id}/label — set the user-facing name on a stack. Trimmed,
 *  non-empty, capped at 60 chars server-side. Returns the accepted name. */
export const labelJob = (id: string, name: string, opts: FetchOpts) =>
  request<{ jobId: string; name: string }>(
    'POST', `/jobs/${encodeURIComponent(id)}/label`, { name }, opts,
  );

// ---- Public share links ----
//
// One active share per succeeded stack. Slug is 96 bits of entropy; the
// public /s/{slug} endpoint is unauthenticated, so the slug IS the auth.
// Owner-authed create/get/delete round out the CRUD from the JobDetail
// "Share" affordance.

export interface JobShare {
  slug: string;
  url: string;         // absolute, e.g. https://pancakestack.net/s/<slug>
  createdAt: string;   // ISO
}

/** POST /jobs/{id}/share — create (or fetch existing) public share link. */
export const createJobShare = (id: string, opts: FetchOpts) =>
  request<JobShare>('POST', `/jobs/${encodeURIComponent(id)}/share`, {}, opts);

/** GET /jobs/{id}/share — 404 if the stack isn't shared. */
export const getJobShare = (id: string, opts: FetchOpts) =>
  request<JobShare>('GET', `/jobs/${encodeURIComponent(id)}/share`, undefined, opts);

/** DELETE /jobs/{id}/share — revoke; idempotent. */
export const deleteJobShare = (id: string, opts: FetchOpts) =>
  request<{ revoked: boolean; slug?: string }>(
    'DELETE', `/jobs/${encodeURIComponent(id)}/share`, undefined, opts,
  );

/** Public share payload — /s/{slug}. No auth. Preview URL is CF-unsigned;
 *  download URL is a short-TTL S3 presign that expires ~1h after fetch. */
export interface PublicShare {
  slug: string;
  collectionId: string;
  stackName: string;
  createdAt: string;
  previewUrl?: string;
  downloadUrl?: string;
  downloadExpires?: string;
  resultFilename?: string;
  masterSizeBytes?: number;
  frameCount: number;
  integrationSeconds: number;
  exptime?: number;
  filter?: string;
  instrume?: string;
  telescop?: string;
  dateObs?: string;
  object?: string;
  // Geek panel — sampled first-non-empty from the collection's FITS
  // metadata. All optional so the SPA skips rows the sensor didn't
  // populate.
  gain?: number;
  offset?: number;
  ccdTemp?: number;   // °C
  setTemp?: number;   // °C
  focalLen?: number;  // mm
  aperture?: number;  // mm
  xPixSz?: number;    // µm
  yPixSz?: number;
  xBinning?: number;
  yBinning?: number;
  width?: number;     // px
  height?: number;
  bitpix?: number;
  ra?: number;        // degrees
  dec?: number;
  airmass?: number;
  moonPhas?: number;  // 0..1 illumination
  poweredBy: string;
}

/** GET /s/{slug} — unauthenticated. Fetches the public share view. */
export const getPublicShare = (slug: string) =>
  request<PublicShare>('GET', `/s/${encodeURIComponent(slug)}`, undefined, {});

/** POST /public/preview/init — anonymous marketing-widget presign. Zero
 *  auth, IP-rate-limited on the backend. Returns a shareId + the
 *  presigned PUT URL for the FITS + the expected preview/thumb URLs the
 *  browser will poll for once the fits-thumbnail Lambda has rendered.
 *  See lambda-start-job/public_preview.go for validation rules and
 *  the 30-day expiry (S3 lifecycle rule on the public-preview prefix). */
export interface PublicPreviewInitReq {
  filename: string;
  sizeBytes: number;
  contentType?: string;
}
export interface PublicPreviewInitResp {
  shareId: string;
  uploadUrl: string;
  key: string;
  previewUrl: string;
  thumbUrl: string;
  expiresIn: number;
  maxBytes: number;
}
export const publicPreviewInit = (body: PublicPreviewInitReq) =>
  request<PublicPreviewInitResp>('POST', '/public/preview/init', body, {});

/** GET /public/preview/{shareId} — unauthenticated share-page metadata.
 *  Lists the S3 prefix on the backend (no DDB); returns everything the
 *  share page needs to render cross-device. The three URLs are
 *  CloudFront (files.pancakestack.net) and unsigned — the raw .fit is
 *  served under a dedicated public-preview/*.fit behavior. */
export interface PublicPreview {
  shareId: string;
  filename: string;
  sizeBytes: number;
  previewUrl: string;
  thumbUrl: string;
  downloadUrl: string;
  createdAt: string;
}
export const getPublicPreview = (shareId: string) =>
  request<PublicPreview>('GET', `/public/preview/${encodeURIComponent(shareId)}`, undefined, {});

export interface CollectionEntry {
  collectionId: string;
  // displayName is what the UI should render everywhere the user sees a
  // collection. collectionId is the immutable S3 prefix / DDB PK segment
  // and is only surfaced in URLs and diagnostics.
  displayName: string;
  archiveFilename?: string;      // e.g. "lights.zip"
  fitsCount?: number;            // individual .fit/.fits under the prefix
  archiveCount?: number;         // .tar/.tar.gz/.tar.zst/.zip
  sizeBytes?: number;
  lastModified?: string;         // ISO
  createdAt?: string;            // ISO — when the DDB row was materialized
  // Archive state — archived collections are read-only from the web,
  // block job starts entirely, and expose an unarchive countdown after
  // a 30-day min duration on Standard-IA. archivedAt is unix seconds.
  archived?: boolean;
  archivedAt?: number;
  // Fine-grained transition state, distinct from `archived`. Written
  // by the async storage-transition Lambda. Values: "" | "archiving" |
  // "archived" | "unarchiving" | "archive_failed" | "unarchive_failed".
  // SPA reads this to render progress pills and disable buttons during
  // the transitional states.
  archiveState?: string;
  archiveError?: string;
}

export interface ArchiveCollectionResp {
  collectionId: string;
  archived: boolean;
  archivedAt: number;
  unarchiveEligibleAt: number;
  // Transitional state written when the API returned 202. The S3
  // storage-class transition runs async in a dedicated Lambda; the SPA
  // polls the collection row to observe archiveState flipping to
  // "archived" (or "archive_failed") when the transition finishes.
  archiveState: string;
}

/** POST /collections/{id}/archive — park the collection: mark archived,
 *  kick the async storage-transition Lambda. Returns 202 with
 *  archiveState="archiving"; SPA polls for terminal state. Requires the
 *  caller to echo the collectionId in the body — type-to-confirm guard. */
export const archiveCollection = (
  id: string,
  confirm: string,
  opts: FetchOpts,
) =>
  request<ArchiveCollectionResp>(
    'POST',
    `/collections/${encodeURIComponent(id)}/archive`,
    { confirm },
    opts,
  );

/** POST /collections/{id}/unarchive — 30 days after archiveAt, restore
 *  the collection to Standard storage. Returns 202 with
 *  archiveState="unarchiving"; async transition Lambda flips
 *  archived=false and drops archiveState when done. Backend enforces
 *  the 30-day gate + a free-quota headroom check before enqueueing. */
export const unarchiveCollection = (id: string, opts: FetchOpts) =>
  request<{ collectionId: string; archived: boolean; archiveState: string }>(
    'POST',
    `/collections/${encodeURIComponent(id)}/unarchive`,
    {},
    opts,
  );

/** GET /collections — list caller's uploaded collections. */
export const listCollections = (opts: FetchOpts) =>
  request<{ collections: CollectionEntry[] }>('GET', '/collections', undefined, opts);

/** DELETE /collections/{id} — remove every S3 object under the collection.
 *  Does NOT delete job history; jobs referencing this collection still exist
 *  but can no longer be retried. */
export const deleteCollection = (id: string, opts: FetchOpts) =>
  request<{ collectionId: string; deleted: number }>(
    'DELETE', `/collections/${encodeURIComponent(id)}`, undefined, opts,
  );

/** POST /collections/{id}/label — set a new displayName on the collection.
 *  The collectionId (S3 prefix, files-table PK) is immutable; only the
 *  human-readable label changes. One DDB UpdateItem — no S3 moves, no
 *  files-table PK rewrites. */
export const labelCollection = (
  id: string,
  displayName: string,
  opts: FetchOpts,
) =>
  request<{ collectionId: string; displayName: string }>(
    'POST',
    `/collections/${encodeURIComponent(id)}/label`,
    { displayName },
    opts,
  );

/** DELETE /collections/{id}/files/{filename} — remove one file plus its
 *  sibling thumb/preview JPEGs and its DDB metadata row. Idempotent. */
export const deleteCollectionFile = (
  id: string,
  filename: string,
  opts: FetchOpts,
) =>
  request<{ collectionId: string; filename: string; deleted: number }>(
    'DELETE',
    `/collections/${encodeURIComponent(id)}/files/${encodeURIComponent(filename)}`,
    undefined,
    opts,
  );

// ---- Bulk upload (drag-drop of N individual FITS files) ----
//
// Backend: POST /upload/presign takes up to 500 filenames, returns N
// presigned single-PUT URLs. Each FITS is ~30 MB, well under S3's 5 GiB
// single-PUT cap, so no multipart machinery needed per file.

export interface PresignBulkFile {
  name: string;
  contentType?: string;
  // SHA-256 of the file bytes, lowercase hex. Required — backend
  // rejects known duplicates before returning a URL.
  sha256: string;
}

export interface PresignedFile {
  name: string;
  key?: string;
  url?: string;
  expiresIn?: number;
  // storageClass, when set, is a value the client MUST send back as the
  // `x-amz-storage-class` header on the PUT — otherwise S3 rejects
  // with 403 SignatureDoesNotMatch (the class is baked into the URL
  // signature). Present only when the presign was issued with
  // createArchived=true (value: "STANDARD_IA").
  storageClass?: string;
  // Backend returns skipped=true when this checksum already exists
  // in the collection. Client must NOT PUT bytes for this entry.
  skipped?: boolean;
  reason?: string;
  duplicateOf?: string;
}

/** POST /upload/presign — batch presign up to 500 filenames.
 *  Set createArchived=true when materializing a brand-new collection
 *  directly into the archived state. Requires the collection not to
 *  exist yet; backend rejects with COLLECTION_EXISTS otherwise. */
export const presignBulk = (
  collectionId: string,
  files: PresignBulkFile[],
  opts: FetchOpts & { createArchived?: boolean },
) =>
  request<{ files: PresignedFile[] }>(
    'POST',
    '/upload/presign',
    { collectionId, files, createArchived: opts.createArchived },
    opts,
  );

// contentTypeFor mirrors the CLI's implementation. Matters because the
// backend bakes Content-Type into the presigned URL — a mismatch on
// PUT returns 403 SignatureDoesNotMatch from S3.
export const contentTypeFor = (name: string): string => {
  const l = name.toLowerCase();
  if (l.endsWith('.fit') || l.endsWith('.fits')) return 'application/fits';
  if (l.endsWith('.fz')) return 'application/octet-stream';
  if (l.endsWith('.zip')) return 'application/zip';
  if (l.endsWith('.tar')) return 'application/x-tar';
  return 'application/octet-stream';
};

export interface BulkUploadResult {
  name: string;
  key?: string;
  error?: string;
  // Backend deduped this file — no PUT was attempted. Not an error.
  skipped?: boolean;
  reason?: string;
  duplicateOf?: string;
}

export interface BulkUploadOptions {
  collectionId: string;
  files: File[];
  concurrency?: number;
  token: string;
  // createArchived=true materializes a brand-new archived collection.
  // Passed through to /upload/presign. Presigned URLs are signed with
  // StorageClass=STANDARD_IA and the client attaches the matching
  // x-amz-storage-class header on PUT.
  createArchived?: boolean;
  // Called each time a file finishes (success, fail, or skipped).
  // `done`/`total` are file counts; `bytesUploaded` tracks transferred
  // bytes only (skipped files contribute nothing).
  onProgress?: (state: {
    done: number;
    total: number;
    bytesUploaded: number;
    totalBytes: number;
    failed: number;
    skipped: number;
  }) => void;
  // Fires whenever the hasher makes progress. Client-side SHA-256 for
  // 3000 files takes 15-30s; UI needs to show life.
  onHashProgress?: (state: { hashed: number; total: number }) => void;
}

// Max files per presign batch — matches backend cap.
const PRESIGN_BATCH_SIZE = 500;

/** uploadFilesBulk — bulk-presign + concurrent PUTs. Returns per-file
 *  results so caller can decide how to surface partial failures.
 *
 *  Scales to 3000+ files: presigns in batches of 500, uploads with
 *  concurrency (default 6). No retry on individual failures for MVP —
 *  caller can re-drop and skip-if-exists would pick them up (future). */
export const uploadFilesBulk = async (
  opts: BulkUploadOptions,
): Promise<BulkUploadResult[]> => {
  const concurrency = opts.concurrency ?? 6;
  const totalBytes = opts.files.reduce((sum, f) => sum + f.size, 0);

  // Hash every FITS client-side first. Backend uses the sha256 to
  // reject duplicates before returning a presigned URL, so the client
  // never spends bandwidth PUTting bytes the collection already has.
  //
  // Archives skip the hash — each archive upload is unique-per-user
  // (they're transport, not content), and hashing a 100+ MB tar wastes
  // seconds of wall time for no benefit. Backend allows empty sha256
  // for archive extensions; see multipart.go presign handler.
  const isArchive = (name: string): boolean => {
    const l = name.toLowerCase();
    return l.endsWith('.tar') || l.endsWith('.zip')
      || l.endsWith('.tar.gz') || l.endsWith('.tgz')
      || l.endsWith('.tar.zst') || l.endsWith('.tar.bz2')
      || l.endsWith('.tar.xz');
  };
  const filesToHash = opts.files.filter((f) => !isArchive(f.name));
  const hashByName = new Map<string, string>();
  const hashResults = await hashFilesInParallel(filesToHash, 4, opts.onHashProgress);
  filesToHash.forEach((f, i) => hashByName.set(f.name, hashResults[i]));
  const shas = opts.files.map((f) => hashByName.get(f.name) ?? '');

  // Presign everything up-front in batches of 500. Simpler than pipelining
  // presign with upload, finishes in a few seconds even for thousands.
  const presigned: PresignedFile[] = [];
  for (let i = 0; i < opts.files.length; i += PRESIGN_BATCH_SIZE) {
    const batch = opts.files.slice(i, i + PRESIGN_BATCH_SIZE);
    const shaSlice = shas.slice(i, i + PRESIGN_BATCH_SIZE);
    const resp = await presignBulk(
      opts.collectionId,
      batch.map((f, j) => ({
        name: f.name,
        contentType: contentTypeFor(f.name),
        sha256: shaSlice[j],
      })),
      { token: opts.token, createArchived: opts.createArchived },
    );
    presigned.push(...resp.files);
  }

  // File name → File object lookup — order-independent since presign
  // response order matches request order but this is safer.
  const byName = new Map(opts.files.map((f) => [f.name, f]));

  const results: BulkUploadResult[] = new Array(presigned.length);
  let done = 0;
  let bytesUploaded = 0;
  let failed = 0;
  let skipped = 0;

  // Simple worker pool: N goroutines pop from a shared queue.
  let cursor = 0;
  const worker = async () => {
    while (true) {
      const i = cursor++;
      if (i >= presigned.length) return;
      const pf = presigned[i];

      // Server said "already have it" — record and move on. Skipped
      // files count toward `done` AND their bytes count toward
      // `bytesUploaded`: totalBytes was computed across every queued
      // file, so if we didn't credit skipped bytes the progress bar
      // (which uses the byte ratio) would lag behind the file counter
      // and hug the left even at 90% done.
      if (pf.skipped) {
        results[i] = {
          name: pf.name,
          skipped: true,
          reason: pf.reason,
          duplicateOf: pf.duplicateOf,
        };
        skipped++;
        done++;
        bytesUploaded += byName.get(pf.name)?.size ?? 0;
        opts.onProgress?.({ done, total: presigned.length, bytesUploaded, totalBytes, failed, skipped });
        continue;
      }

      const file = byName.get(pf.name)!;
      try {
        await putPresigned(pf.url!, file, contentTypeFor(pf.name), pf.storageClass);
        results[i] = { name: pf.name, key: pf.key };
      } catch (e) {
        results[i] = { name: pf.name, key: pf.key, error: (e as Error).message };
        failed++;
      }
      done++;
      bytesUploaded += file.size;
      opts.onProgress?.({ done, total: presigned.length, bytesUploaded, totalBytes, failed, skipped });
    }
  };
  const workers = Array.from({ length: Math.min(concurrency, presigned.length) }, worker);
  await Promise.all(workers);
  return results;
};

// hashFilesInParallel computes SHA-256 for each File. crypto.subtle
// takes an ArrayBuffer, so we read the file into memory once (typical
// FITS is 20-40 MB — fine) and hash. Bounded concurrency so 2500
// simultaneous reads don't OOM the browser.
export const hashFilesInParallel = async (
  files: File[],
  concurrency: number,
  onProgress?: (state: { hashed: number; total: number }) => void,
): Promise<string[]> => {
  const results = new Array<string>(files.length);
  let cursor = 0;
  let hashed = 0;
  const total = files.length;
  const worker = async () => {
    while (true) {
      const i = cursor++;
      if (i >= files.length) return;
      results[i] = await hashFile(files[i]);
      hashed++;
      onProgress?.({ hashed, total });
    }
  };
  const workers = Array.from({ length: Math.min(concurrency, files.length) }, worker);
  await Promise.all(workers);
  return results;
};

// hashFile returns the lowercase-hex SHA-256 of a File's bytes.
export const hashFile = async (file: File | Blob): Promise<string> => {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  const bytes = new Uint8Array(digest);
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, '0');
  }
  return out;
};

const putPresigned = async (
  url: string,
  blob: Blob,
  contentType: string,
  storageClass?: string,
): Promise<void> => {
  const headers: Record<string, string> = { 'Content-Type': contentType };
  if (storageClass) {
    // The backend signed the URL with StorageClass=<value> as a signed
    // header. S3 will 403 the PUT unless we echo the exact same value
    // as x-amz-storage-class.
    headers['x-amz-storage-class'] = storageClass;
  }
  const resp = await fetch(url, {
    method: 'PUT',
    headers,
    body: blob,
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`S3 ${resp.status}: ${text.slice(0, 200)}`);
  }
};

export interface CollectionFile {
  name: string;
  sizeBytes: number;
  lastModified: string;
  kind: 'fits' | 'archive' | 'other';
  // Signed CloudFront URLs to sibling JPEGs generated by the fits-thumbnail
  // Lambda. Absent while the SQS queue is still draining, or if the render
  // failed. Client should render a placeholder for missing URLs.
  thumbUrl?: string;
  previewUrl?: string;
  // FITS header + pixel stats — present when the DDB metadata row exists
  // (populated by the fits-thumbnail Lambda). Absent under the S3
  // fallback listing (older collections that predate the Lambda). All
  // fields the Lambda can extract are declared here; Seestar rows fill
  // in the core subset, non-Seestar setups populate more.
  object?: string;
  dateObs?: string;
  exptime?: number;
  gain?: number;
  offset?: number;
  ccdTemp?: number;
  setTemp?: number;
  filter?: string;
  imagetyp?: string;
  instrume?: string;
  telescop?: string;
  swCreate?: string;
  bayerPat?: string;
  focalLen?: number;
  aperture?: number;
  xPixSz?: number;
  yPixSz?: number;
  xBinning?: number;
  yBinning?: number;
  ra?: number;
  dec?: number;
  airmass?: number;
  moonPhas?: number;
  siteLat?: number;
  siteLong?: number;
  width?: number;
  height?: number;
  bitpix?: number;
  mean?: number;
  median?: number;
  stddev?: number;
  min?: number;
  max?: number;
  // Quality metrics from the Python analyzer Lambda (sep + astropy).
  // Absent until the analyzer runs — arrives asynchronously via SQS
  // seconds after the Go Lambda's fields.
  starCount?: number;
  fwhmMedian?: number;
  eccentricityMedian?: number;
  snrMedian?: number;
  background?: number;
  backgroundRms?: number;
  saturatedPct?: number;
}

export interface ListCollectionFilesResp {
  files: CollectionFile[];
  nextToken?: string;
  // 'ddb' when the response came from the metadata table (rich fields
  // available); 's3' when we fell back to bucket listing (name/size only).
  source: 'ddb' | 's3';
  // Present when a background archive op is in flight — either CLI
  // multipart upload streaming in, or a fully-uploaded tar that the
  // normalize Lambda's spot instance is unpacking. Absent (undefined)
  // when the collection is idle. Archives themselves are filtered out
  // of `files` — the banner replaces the misleading row.
  archive?: {
    state: 'uploading' | 'extracting';
    bytes?: number;
    since?: string;
  };
}

/** GET /collections/{id}/files — paginated files with FITS metadata.
 *  Does NOT include signed thumb/preview URLs (signing 200+ URLs made
 *  the endpoint slow). Fetch a single file's URLs via getCollectionFile
 *  when the lightbox opens. */
export const listCollectionFiles = (
  id: string,
  opts: FetchOpts & { nextToken?: string; pageSize?: number },
) => {
  const qs = new URLSearchParams();
  if (opts.nextToken) qs.set('nextToken', opts.nextToken);
  if (opts.pageSize) qs.set('pageSize', String(opts.pageSize));
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<ListCollectionFilesResp>(
    'GET', `/collections/${encodeURIComponent(id)}/files${suffix}`, undefined, opts,
  );
};

/** GET /collections/{id}/files/{filename} — one file with signed URLs.
 *  Called by the lightbox on open to get the preview JPEG URL without
 *  paying signing latency on the whole list endpoint. */
export const getCollectionFile = (
  id: string,
  filename: string,
  opts: FetchOpts,
) =>
  request<CollectionFile>(
    'GET',
    `/collections/${encodeURIComponent(id)}/files/${encodeURIComponent(filename)}`,
    undefined,
    opts,
  );

export interface LogEvent {
  timestamp: number;   // ms since epoch
  message: string;
}

export interface LogsResponse {
  source: 'job' | 'cloudinit';
  hasStream: boolean;
  events: LogEvent[];
  nextForwardToken?: string;
  nextBackwardToken?: string;
}

/**
 * GET /logs/{id} — CloudWatch events for a job's stream.
 *
 * Pass `nextToken` from the previous response to forward-paginate; omit for
 * the initial fetch. `source` defaults to 'job' (siril-job.log).
 *
 * Selection knobs (all optional):
 *   startFromHead  false = return the tail (last `limit` events) — what you
 *                  want on the *first* fetch so the user isn't reading boot
 *                  chatter on a long-running job. Server default is true.
 *   startTime      ms epoch, inclusive lower bound on event timestamp.
 *   endTime        ms epoch, exclusive upper bound.
 */
// ---- Personal access tokens (PATs) ----
//
// Per-user API credentials for CLI / script access. Mint returns the raw
// token string exactly once; list and revoke only ever see the display
// prefix. Revoke is idempotent — the response shape is the same whether
// the token existed and was deleted or never existed at all (prevents
// enumeration of another user's tokenIds).

export interface PatSummary {
  tokenId: string;
  name: string;
  prefix: string;          // e.g. "pk_live_a3f2" — safe to display
  createdAt: string;       // ISO
  lastUsedAt?: string;     // ISO; absent = never used since mint
}

export interface MintPatResp extends PatSummary {
  // Raw token string. Present ONLY on the mint response — the whole point
  // of a PAT is that this string never leaves the user's hands. UI must
  // show once, hide behind a copy-to-clipboard affordance, and forget.
  token: string;
}

/** POST /pats — mint a new personal access token. */
export const mintPat = (name: string, opts: FetchOpts) =>
  request<MintPatResp>('POST', '/pats', { name }, opts);

/** GET /pats — list caller's PATs, newest first. */
export const listPats = (opts: FetchOpts) =>
  request<{ pats: PatSummary[] }>('GET', '/pats', undefined, opts);

/** DELETE /pats/{id} — revoke by tokenId. Idempotent. */
export const revokePat = (tokenId: string, opts: FetchOpts) =>
  request<{ tokenId: string; revoked: boolean }>(
    'DELETE', `/pats/${encodeURIComponent(tokenId)}`, undefined, opts,
  );

export const getLogs = (
  id: string,
  opts: FetchOpts & {
    source?: 'job' | 'cloudinit';
    nextToken?: string;
    limit?: number;
    startFromHead?: boolean;
    startTime?: number;
    endTime?: number;
  } = {},
) => {
  const qs = new URLSearchParams();
  if (opts.source)                   qs.set('source',        opts.source);
  if (opts.nextToken)                qs.set('nextToken',     opts.nextToken);
  if (opts.limit)                    qs.set('limit',         String(opts.limit));
  if (opts.startFromHead !== undefined) qs.set('startFromHead', String(opts.startFromHead));
  if (opts.startTime)                qs.set('startTime',     String(opts.startTime));
  if (opts.endTime)                  qs.set('endTime',       String(opts.endTime));
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<LogsResponse>(
    'GET', `/logs/${encodeURIComponent(id)}${suffix}`, undefined, opts,
  );
};
